import { useState, useCallback, useMemo, Fragment } from 'react';
import type { ReactNode, TableHTMLAttributes, ComponentType, MouseEvent } from 'react';

import clsx from 'clsx';
import { Tooltip } from 'react-tooltip';
import {
  ChevronDown,
  PlusCircle,
  SquarePen,
  Trash2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';

import { RequestStateValue } from './state-request-value';
import { GradeStateValue } from './state-grade-value';
import { StatusValue } from './status-value';
import { Button } from './button';

type TableValue = unknown;
type TableRow = Record<string, TableValue>;
type TableData = TableRow[];

type BaseHeader = {
  key: string;
  label?: string;
  format?: (value: TableValue, row?: TableRow) => ReactNode;
  tooltip?: (value: TableRow) => ReactNode;
  action?: ReactNode;
};

type TableHeaderAction = BaseHeader & {
  type: 'actions';
  edit?: (row: TableRow) => void;
  delete?: (row: TableRow) => void;
  add?: (row: TableRow) => void;
  disabled?: (row: TableRow) => boolean; // custom disable action for the actions on this row
};

type TableHeaderState = BaseHeader & {
  type: 'request-state' | 'grade-state' | 'status';
};

type TableHeaderCollapsible = BaseHeader & {
  type: 'collapsible';
  value?: (row?: TableRow) => ReactNode;
  hide?: (row?: TableRow) => boolean;
};

type TableHeaderSummaryRow = BaseHeader & {
  type: 'summary';
  value: (row?: TableRow) => ReactNode;
};

type TableHeaderValue = BaseHeader & {
  type?: 'value';
  sortFn?: (rowA: TableRow, rowB: TableRow) => number;
};

type TableHeader =
  | TableHeaderValue
  | TableHeaderAction
  | TableHeaderState
  | TableHeaderCollapsible
  | TableHeaderSummaryRow;

type TableProps<T extends TableData> = TableHTMLAttributes<HTMLTableElement> & {
  headers: TableHeader[];
  data: T;
  bodyClassName?: string;
  cellClassName?: string;
  onRowClick?: (item: T[number]) => void;
  CollapsibleComponent?: ComponentType<{ item: T[number] }>;
  disabled?: boolean; // Global table indicator for disabling actions
};

export const Table = function Table<T extends TableData>(props: TableProps<T>) {
  const {
    headers = [],
    data = [],
    onRowClick,
    bodyClassName = '',
    cellClassName = '',
    className = '',
    CollapsibleComponent,
    disabled = false,
    ...rest
  } = props;
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [sortState, setSortState] = useState<{
    columnKey: string | null;
    direction: 'asc' | 'desc' | null;
  }>({
    columnKey: null,
    direction: null,
  });

  const toggleRow = useCallback(
    (index: number) => {
      setExpandedRows((prev) =>
        prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
      );
    },
    [setExpandedRows]
  );

  const handleSort = useCallback((columnKey: string) => {
    setSortState((prev) => {
      // If clicking a different column, start with ascending
      if (prev.columnKey !== columnKey) {
        return { columnKey, direction: 'asc' };
      }
      // If currently ascending, switch to descending
      if (prev.direction === 'asc') {
        return { columnKey, direction: 'desc' };
      }
      // If currently descending, reset to unsorted
      if (prev.direction === 'desc') {
        return { columnKey: null, direction: null };
      }
      // If unsorted, start with ascending
      return { columnKey, direction: 'asc' };
    });
  }, []);

  const sortedData = useMemo(() => {
    if (!sortState.columnKey || !sortState.direction) return data;

    const header = headers.find((h) => h.key === sortState.columnKey);
    if (!header) return data;

    // Check if this header is a value type with sortFn
    if (header.type && header.type !== 'value') return data;
    if (!('sortFn' in header) || !header.sortFn) return data;

    const sorted = [...data].sort(header.sortFn);
    return sortState.direction === 'desc' ? sorted.reverse() : sorted;
  }, [data, sortState, headers]);

  return (
    <table
      className={clsx('w-full caption-bottom text-sm table-auto border-collapse', className)}
      {...rest}
    >
      <thead className="[&_tr]:border-b">
        <tr>
          {headers.map((header) => {
            const key = `table-header-${header.key}`;
            const isActionsColumn = header.type === 'actions';
            const isCollapsibleColumn = header.type === 'collapsible';
            const isSortable =
              !isActionsColumn &&
              !isCollapsibleColumn &&
              'sortFn' in header &&
              header.sortFn !== undefined;

            const isSorted = sortState.columnKey === header.key;
            const sortDirection = isSorted ? sortState.direction : null;

            const getSortIcon = () => {
              if (!isSorted || !sortDirection) return <ArrowUpDown className="ml-2 h-4 w-4" />;
              if (sortDirection === 'asc') return <ArrowUp className="ml-2 h-4 w-4" />;
              return <ArrowDown className="ml-2 h-4 w-4" />;
            };

            const getSortLabel = () => {
              const columnName = header.label ?? header.key;
              if (!isSorted || !sortDirection) return `Sort by ${columnName}`;
              if (sortDirection === 'asc')
                return `${columnName} sorted ascending, click to sort descending`;
              return `${columnName} sorted descending, click to clear sort`;
            };

            return (
              <th
                key={key}
                className="uppercase tracking-wider h-10 px-4 text-xs text-left text-gray-500 dark:text-foreground align-middle font-medium whitespace-nowrap"
              >
                {!isActionsColumn && !isCollapsibleColumn ? (
                  isSortable ? (
                    <button
                      type="button"
                      onClick={() => handleSort(header.key)}
                      className="inline-flex items-center hover:bg-muted/50 rounded px-2 py-1 -mx-2 -my-1 transition-colors"
                      aria-label={getSortLabel()}
                      data-testid={`table-header-sort-${header.key}`}
                    >
                      {header.label ?? header.key}
                      {getSortIcon()}
                      {header.action ? <span className="ml-1">{header.action}</span> : null}
                    </button>
                  ) : (
                    <div className="inline-flex items-center">
                      {header.label ?? header.key}
                      {header.action ? <span className="ml-1">{header.action}</span> : null}
                    </div>
                  )
                ) : isCollapsibleColumn ? (
                  header.label
                ) : null}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody className={bodyClassName}>
        {sortedData.map((row, index) => (
          <Fragment key={`table-row-group-${index}`}>
            <tr
              key={`table-row-${index}`}
              className="hover:bg-muted/50 border-b transition-colors"
              onClick={onRowClick && !disabled ? () => onRowClick(row) : undefined}
              role={onRowClick && !disabled ? 'button' : undefined}
              tabIndex={onRowClick && !disabled ? 0 : undefined}
              style={onRowClick && !disabled ? { cursor: 'pointer' } : undefined}
              aria-expanded={expandedRows.includes(index)}
            >
              {headers.map((header) => {
                const key = `table-row-${index}-${header.key}`;
                const tooltipId = `${key}-tooltip`;

                if (header.type === 'actions') {
                  const actionsDisabled = header.disabled ? header.disabled(row) : false;

                  return (
                    <td
                      key={key}
                      className="px-4 align-middle whitespace-nowrap text-gray-600 hover:text-gray-900"
                    >
                      {header.edit ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={disabled || actionsDisabled}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            header.edit?.(row);
                          }}
                        >
                          <SquarePen />
                        </Button>
                      ) : null}
                      {header.delete ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={disabled || actionsDisabled}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            header.delete?.(row);
                          }}
                        >
                          <Trash2 />
                        </Button>
                      ) : null}
                      {header.add ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={disabled || actionsDisabled}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            header.add?.(row);
                          }}
                        >
                          <PlusCircle />
                        </Button>
                      ) : null}
                    </td>
                  );
                }

                if (header.type === 'collapsible') {
                  // Sometimes we want to hide the collapsible row, for example when there are no data to show
                  const shouldHideCollapsible = header.hide ? header.hide(row) : false;
                  if (shouldHideCollapsible) return <td key={key} className="py-4 px-4" />;

                  const collapsibleKey = `table-row-${index}-collapsible-${header.key}`;
                  const collapsibleLabel = expandedRows.includes(index)
                    ? 'Collapse ' + header.label
                    : 'Expand ' + header.label;

                  const handleCollapseClick = (e: MouseEvent) => {
                    e.stopPropagation();
                    toggleRow(index);
                  };

                  return (
                    <td key={key} className="py-4 px-4 align-middle whitespace-nowrap w-[124px]">
                      <Button
                        size="icon"
                        onClick={handleCollapseClick}
                        aria-label={collapsibleLabel}
                        data-tooltip-id={collapsibleKey}
                        disabled={disabled}
                        variant="ghost"
                      >
                        <ChevronDown
                          className={clsx(
                            'inline transform transition-transform duration-300',
                            expandedRows.includes(index) ? 'rotate-180' : 'rotate-0'
                          )}
                        />
                      </Button>
                      {header.value ? header.value(row) : null}

                      <Tooltip id={collapsibleKey} place="top">
                        {collapsibleLabel}
                      </Tooltip>
                    </td>
                  );
                }

                const value = header.format ? header.format(row[header.key], row) : row[header.key];

                const tooltip = header.tooltip ? (
                  <Tooltip id={key} place="top" clickable>
                    {header.tooltip(row)}
                  </Tooltip>
                ) : null;

                if (header.type === 'status') {
                  return (
                    <td key={key} data-tooltip-id={key}>
                      <span aria-describedby={tooltipId}>
                        <StatusValue value={value as boolean} />
                      </span>
                      {tooltip}
                    </td>
                  );
                }

                if (header.type === 'request-state') {
                  return (
                    <td key={key} data-tooltip-id={key}>
                      <span aria-describedby={tooltipId}>
                        <RequestStateValue value={value as string} />
                      </span>
                      {tooltip}
                    </td>
                  );
                }

                if (header.type === 'grade-state') {
                  return (
                    <td key={key} data-tooltip-id={key}>
                      <span aria-describedby={tooltipId}>
                        <GradeStateValue value={value as number} />
                      </span>
                      {tooltip}
                    </td>
                  );
                }

                if (header.type === 'summary') {
                  return (
                    <td key={key} data-tooltip-id={key}>
                      <span aria-describedby={tooltipId}>{header.value(row)}</span>
                      {tooltip}
                    </td>
                  );
                }

                const plainValue = (value as ReactNode) ?? '-';

                return (
                  <td
                    key={key}
                    className={clsx('py-4 px-4 align-middle whitespace-nowrap', cellClassName)}
                  >
                    <div
                      aria-describedby={tooltipId}
                      data-tooltip-id={key}
                      className="text-ellipsis overflow-hidden text-nowrap w-max"
                    >
                      {typeof plainValue === 'string' ? String(plainValue) : plainValue}
                    </div>
                    {tooltip}
                  </td>
                );
              })}
            </tr>

            {expandedRows.includes(index) && CollapsibleComponent && (
              <tr className="bg-muted/50">
                <td colSpan={headers.length} className="px-8 py-4">
                  <CollapsibleComponent item={row} />
                </td>
              </tr>
            )}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
};
